import { memo } from 'react';
import type { Message } from '@shared/types';
import { cn } from '@web/lib/cn';
import { fmtClock } from '@web/lib/time';

/**
 * One message.
 *
 * Four voices, and they must be distinguishable without reading the label:
 *
 *  - the candidate, left, on the raised surface;
 *  - the assistant, right, hairline-outlined and unfilled — it is a machine and
 *    it should look like one;
 *  - a colleague, right, filled — a person put their name to this;
 *  - the system, centred as a thin rule, because a takeover is an event in the
 *    conversation rather than a turn in it.
 *
 * Internal notes are the fifth case and the one that has to be unmistakable:
 * amber, a bar down the side, and the words NEVER SENT in the header. A note
 * that an operator believes was sent — or that a candidate ever sees — is the
 * worst thing this screen can do.
 */

const STATUS_MARK: Record<string, string> = {
  queued: '···',
  sent: '✓',
  delivered: '✓✓',
  read: '✓✓',
  failed: '!',
};

function SystemEvent({ message }: { message: Message }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span aria-hidden className="h-px flex-1 bg-rule" />
      <span className="font-mono text-[10px] tracking-[0.08em] text-ink-faint">
        {message.body ?? 'event'} · {fmtClock(message.createdAt)}
      </span>
      <span aria-hidden className="h-px flex-1 bg-rule" />
    </div>
  );
}

export const MessageBubble = memo(function MessageBubble({
  message,
  authorName,
}: {
  message: Message;
  /** Resolved staff name for an agent message, when the console knows it. */
  authorName?: string;
}) {
  if (message.senderType === 'system' || message.type === 'system_event') {
    return <SystemEvent message={message} />;
  }

  const note = message.isPrivate || message.type === 'note';
  const fromCandidate = message.senderType === 'contact';

  return (
    <div
      className={cn('flex px-3 py-1', fromCandidate ? 'justify-start' : 'justify-end')}
      data-sender={message.senderType}
    >
      <div
        className={cn(
          'max-w-[min(78%,34rem)] border px-3 py-2',
          note && 'border-warm border-l-[3px] bg-warm/10',
          !note && fromCandidate && 'border-rule bg-raised',
          !note && message.senderType === 'ai' && 'border-rule-strong bg-transparent',
          !note && message.senderType === 'agent' && 'border-ink-dim bg-ink-dim/15',
        )}
      >
        <div className="mb-1 flex items-baseline gap-2 font-mono text-[10px] tracking-[0.08em]">
          <span
            className={cn(
              note ? 'text-warm' : fromCandidate ? 'text-ink-faint' : 'text-ink-dim',
              'font-medium',
            )}
          >
            {note
              ? 'NOTE · NEVER SENT'
              : fromCandidate
                ? 'CANDIDATE'
                : message.senderType === 'ai'
                  ? 'ASSISTANT'
                  : (authorName?.toUpperCase() ?? 'COLLEAGUE')}
          </span>
          <span className="text-ink-faint tabular-nums">{fmtClock(message.createdAt)}</span>
          {!fromCandidate && !note && message.deliveryStatus !== null && (
            <span
              className={cn(
                'tabular-nums',
                message.deliveryStatus === 'failed' ? 'text-signal' : 'text-ink-faint',
              )}
              aria-label={`delivery ${message.deliveryStatus}`}
            >
              <span aria-hidden>{STATUS_MARK[message.deliveryStatus] ?? ''}</span>
            </span>
          )}
        </div>

        <div className="text-[13px] leading-[1.5] whitespace-pre-wrap text-ink">
          {message.body ?? <span className="text-ink-faint italic">({message.type})</span>}
        </div>

        {message.failureReason !== null && (
          <div className="mt-1 font-mono text-[10px] text-signal">{message.failureReason}</div>
        )}
      </div>
    </div>
  );
});

/** The hairline that separates one calendar day from the next. */
export function DayDivider({ iso }: { iso: string }) {
  const date = new Date(iso);
  const text = Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return (
    <div className="flex items-center gap-3 px-4 pt-3 pb-1">
      <span aria-hidden className="h-px flex-1 bg-rule" />
      <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
        {text}
      </span>
      <span aria-hidden className="h-px flex-1 bg-rule" />
    </div>
  );
}
